/** Minimal fields for revision letter and workbench list grouping (no API imports). */

export interface DocumentForRevisionGrouping {
  id: string;
  title: string;
  document_kind_code: string | null;
  /** Stored upload name. Same title can be a PDF and a schematic; extension keeps them apart. */
  original_filename?: string | null;
  revision: string;
  lifecycle_status: "draft" | "wip" | "current" | "obsolete";
  updated_at: string;
  targets?: Array<{
    target_type: "sku" | "collection";
    sku: string | null;
    product_collection_id: string | null;
  }>;
}

export function documentTitleAndKindKey(document: DocumentForRevisionGrouping): string {
  const kind = document.document_kind_code ?? "";
  const title = document.title.trim().toLowerCase();
  return `${kind}\u0000${title}`;
}

/** True when two rows share at least one applicability target (matches revision history SQL). */
export function documentTargetsOverlap(
  documentA: DocumentForRevisionGrouping,
  documentB: DocumentForRevisionGrouping,
): boolean {
  const targetsA = documentA.targets ?? [];
  const targetsB = documentB.targets ?? [];
  for (const targetA of targetsA) {
    for (const targetB of targetsB) {
      if (targetA.target_type !== targetB.target_type) continue;
      if (targetA.target_type === "sku" && targetA.sku && targetA.sku === targetB.sku) return true;
      if (
        targetA.target_type === "collection" &&
        targetA.product_collection_id &&
        targetA.product_collection_id === targetB.product_collection_id
      ) {
        return true;
      }
    }
  }
  return false;
}

/** Lowercase extension with no leading dot, or empty when the filename has none. */
export function documentFileExtensionKey(filename: string | null | undefined): string {
  if (!filename) return "";
  const base = filename.replace(/^.*[/\\]/, "");
  const lastDot = base.lastIndexOf(".");
  if (lastDot <= 0 || lastDot >= base.length - 1) return "";
  return base.slice(lastDot + 1).trim().toLowerCase();
}

/** Same controlled document line as revision history: title, type, file extension, and overlapping targets. */
export function documentSharesRevisionLineage(
  documentA: DocumentForRevisionGrouping,
  documentB: DocumentForRevisionGrouping,
): boolean {
  if (documentTitleAndKindKey(documentA) !== documentTitleAndKindKey(documentB)) return false;
  if (documentFileExtensionKey(documentA.original_filename) !== documentFileExtensionKey(documentB.original_filename)) {
    return false;
  }
  return documentTargetsOverlap(documentA, documentB);
}

function findUnionFindRoot(parentById: Map<string, string>, nodeId: string): string {
  let current = nodeId;
  while (parentById.get(current) !== current) {
    current = parentById.get(current) ?? current;
  }
  let root = current;
  let walk = nodeId;
  while (walk !== root) {
    const next = parentById.get(walk) ?? walk;
    parentById.set(walk, root);
    walk = next;
  }
  return root;
}

function uniteUnionFind(parentById: Map<string, string>, idA: string, idB: string): void {
  const rootA = findUnionFindRoot(parentById, idA);
  const rootB = findUnionFindRoot(parentById, idB);
  if (rootA !== rootB) parentById.set(rootB, rootA);
}

/** Parses A–Z revision letters (A=1, B=2, …, Z=26, AA=27). Returns null for non-letter revisions. */
export function parseRevisionLetter(revision: string): number | null {
  const trimmed = revision.trim().toUpperCase();
  if (!/^[A-Z]+$/.test(trimmed)) return null;
  let value = 0;
  for (let index = 0; index < trimmed.length; index += 1) {
    value = value * 26 + (trimmed.charCodeAt(index) - 64);
  }
  return value;
}

/** Inverse of {@link parseRevisionLetter} for values >= 1. */
export function formatRevisionLetter(value: number): string {
  let remaining = value;
  let letters = "";
  while (remaining > 0) {
    remaining -= 1;
    letters = String.fromCharCode(65 + (remaining % 26)) + letters;
    remaining = Math.floor(remaining / 26);
  }
  return letters || "A";
}

/** Next revision letter after the highest A–Z value in `existing`, or `A` when none parse. */
export function nextRevisionLetter(existing: Iterable<string>): string {
  let max = 0;
  for (const revision of existing) {
    const parsed = parseRevisionLetter(revision);
    if (parsed != null && parsed > max) max = parsed;
  }
  return formatRevisionLetter(max + 1);
}

export interface ControlledDocumentListGroup<T extends DocumentForRevisionGrouping> {
  key: string;
  representative: T;
  revisions: T[];
}

function pickNewestByUpdatedAt<T extends DocumentForRevisionGrouping>(revisions: T[]): T {
  return revisions.reduce((best, revision) =>
    new Date(revision.updated_at).getTime() > new Date(best.updated_at).getTime() ? revision : best,
  );
}

function pickRepresentativeDocument<T extends DocumentForRevisionGrouping>(revisions: T[]): T {
  const released = revisions.filter((revision) => revision.lifecycle_status === "current");
  if (released.length > 0) {
    return pickNewestByUpdatedAt(released);
  }
  const active = revisions.filter((revision) => revision.lifecycle_status !== "obsolete");
  const pool = active.length > 0 ? active : revisions;
  return pickNewestByUpdatedAt(pool);
}

/** One workbench row per logical document. Revisions share title, type, file extension, and any target. */
export function groupDocumentsForList<T extends DocumentForRevisionGrouping>(
  documents: T[],
): ControlledDocumentListGroup<T>[] {
  const parentById = new Map<string, string>();
  for (const document of documents) {
    parentById.set(document.id, document.id);
  }
  for (let indexA = 0; indexA < documents.length; indexA += 1) {
    for (let indexB = indexA + 1; indexB < documents.length; indexB += 1) {
      const documentA = documents[indexA]!;
      const documentB = documents[indexB]!;
      if (documentSharesRevisionLineage(documentA, documentB)) {
        uniteUnionFind(parentById, documentA.id, documentB.id);
      }
    }
  }
  const byRoot = new Map<string, T[]>();
  for (const document of documents) {
    const root = findUnionFindRoot(parentById, document.id);
    const list = byRoot.get(root) ?? [];
    list.push(document);
    byRoot.set(root, list);
  }
  return [...byRoot.entries()].map(([key, revisions]) => ({
    key,
    revisions,
    representative: pickRepresentativeDocument(revisions),
  }));
}

/** Released (`current`) revision letter for a grouped list row, or null when none is active. */
export function documentListActiveRevision<T extends DocumentForRevisionGrouping>(revisions: T[]): string | null {
  const released = revisions.filter((revision) => revision.lifecycle_status === "current");
  if (released.length === 0) return null;
  const sorted = [...released].sort((left, right) => {
    const parsedLeft = parseRevisionLetter(left.revision);
    const parsedRight = parseRevisionLetter(right.revision);
    if (parsedLeft != null && parsedRight != null && parsedLeft !== parsedRight) {
      return parsedRight - parsedLeft;
    }
    return new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime();
  });
  const letter = sorted[0]!.revision.trim();
  return letter.length > 0 ? letter : null;
}

/** Label for the active revision column (`—` when no released revision). */
export function documentListActiveRevisionLabel<T extends DocumentForRevisionGrouping>(revisions: T[]): string {
  const active = documentListActiveRevision(revisions);
  return active ?? "—";
}

/** Comma-separated revision letters for a grouped list row, sorted A→Z. */
export function documentListRevisionSummary<T extends DocumentForRevisionGrouping>(revisions: T[]): string {
  const letters = [
    ...new Set(
      revisions
        .map((revision) => revision.revision.trim())
        .filter((revision) => revision.length > 0),
    ),
  ].sort((left, right) => {
    const parsedLeft = parseRevisionLetter(left);
    const parsedRight = parseRevisionLetter(right);
    if (parsedLeft != null && parsedRight != null) return parsedLeft - parsedRight;
    return left.localeCompare(right, undefined, { sensitivity: "base" });
  });
  return letters.join(", ");
}

/** Status chip for a grouped row: surface in-flight work before released or obsolete. */
export function documentListStatusSummary<T extends DocumentForRevisionGrouping>(
  revisions: T[],
): T["lifecycle_status"] {
  const statuses = new Set(revisions.map((revision) => revision.lifecycle_status));
  if (statuses.has("draft")) return "draft";
  if (statuses.has("wip")) return "wip";
  if (statuses.has("current")) return "current";
  return "obsolete";
}
